import React, { useState } from "react";
import shortid from "shortid";
import styled from "styled-components";

import {
  Callout,
  CautionBanner,
  CheckboxInput,
  Form,
  InfoBanner,
  Label,
  Modal,
  ModalProps,
  SelectInput,
  SubmitButtons,
  TextArea,
  useVolatileCallback,
  VSpace,
} from "@examspark/common-ui";
import { FeedbackInput } from "@examspark/common-ui/dist/components/inputs";
import { LabelText } from "@examspark/common-ui/dist/components/inputs/Label";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { PrintExamInput } from "@examspark/types";

import { useModal } from "../../hooks/useModal";
import { logger } from "../../util";
import PrintModal, { PrintModalRequest } from "../PrintModal";

const StyledCautionBanner = styled(CautionBanner)`
  border: solid 1px #ddd;
  border-radius: 0.5rem;
`;

const StyledInfoBanner = styled(InfoBanner)`
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  & ul {
    margin: 0;
  }
`;

const StyledCheckboxLabel = styled(Label)`
  & > input[type="checkbox"] {
    flex-grow: 0;
    order: -1;
  }

  & > ${LabelText} {
    text-transform: initial;
  }
`;

const StyledCheckboxes = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const StyledFeedback = styled.div`
  font-family:
    proxima-nova,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    "Roboto",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    sans-serif;
`;

export type PrintFlags = Partial<{
  showShuffleItems: boolean;
}>;
const PrintExamModal = (
  props: ModalProps & {
    examId: string;
    publisherName?: string | null;
    workspaceName?: string | null;
    workspaceId?: string | null;
    username?: string | null;
    requestFeedback?: boolean;
    flags: PrintFlags;
  },
) => {
  const [theme, setTheme] = useState("traditional");
  const [printExam, setPrintExam] = useState(true);
  const [printAnswerKey, setPrintAnswerKey] = useState(false);
  const [printDualColumn, setPrintDualColumn] = useState(false);
  const [printShuffleQuestions, setPrintShuffleQuestions] = useState(false);

  const feedbackModal = useModal();
  const [showPromptFeedback, setShowPromptFeedback] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState<1 | 0>();
  const [feedbackText, setFeedbackText] = useState("");

  const handleSubmitFeedback = () => {
    logger.info(`Print Exam to PDF Feedback: ${feedbackText}`, {
      examId: props.examId,
      workspaceId: props.workspaceId ?? "",
      feedbackValue,
    });

    feedbackModal.close();
    props.onRequestClose?.();
  };

  const onRequestPrint = useVolatileCallback(() => {
    const today = new Date();
    const requests: PrintModalRequest[] = [];

    const commonRenderArgs = {
      theme,
      publisherName: props.publisherName,
      workspace: props.workspaceName ?? "",
      workspaceId: props.workspaceId ?? "",
      copyrightYear: today.getFullYear().toString(),
      date: new Intl.DateTimeFormat("en-US").format(today),
      username: props.username ?? "",
    };

    const shuffleSettings: Partial<PrintExamInput> = {};
    if (printShuffleQuestions) {
      shuffleSettings["shouldShuffleItemOrder"] = true;
      shuffleSettings["shuffleSeed"] = shortid.generate();
    }

    if (printExam) {
      requests.push({
        action: "PrintExam",
        label: "Assessment",
        params: {
          examId: props.examId,
          answerKey: false,
          dualColumn: printDualColumn,
          ...commonRenderArgs,
          ...shuffleSettings,
        },
      });

      logger.info("Request Print Exam to PDF", {
        examId: props.examId,
        workspaceId: props.workspaceId ?? "",
      });
    }

    if (printAnswerKey) {
      requests.push({
        action: "PrintExamAnswerKey",
        label: "Answer Key",
        params: {
          examId: props.examId,
          answerKey: true,
          dualColumn: false,
          ...commonRenderArgs,
          ...shuffleSettings,
        },
      });
    }

    if (props.requestFeedback) {
      setShowPromptFeedback(true);
    }
    return requests;
  });

  if (!props.workspaceId) {
    return (
      <PrintModal
        title="Print Assessment"
        size="small"
        requestPrintDisabled={true}
        {...props}
      >
        <PaddedBody>
          <VSpace>
            <StyledCautionBanner>
              We could not authorize you to print this exam.
            </StyledCautionBanner>
          </VSpace>
        </PaddedBody>
      </PrintModal>
    );
  }

  return (
    <>
      <PrintModal
        title="Print Assessment"
        size="small"
        onRequestPrint={onRequestPrint}
        {...props}
        onRequestClose={(e) => {
          if (showPromptFeedback) {
            feedbackModal.open();
            setShowPromptFeedback(false);
            return;
          }

          props.onRequestClose?.(e);
        }}
      >
        <PaddedBody>
          <VSpace>
            <StyledCautionBanner>
              <p>
                {/* This is an early preview of an in development feature. */}
                Unsupported items may not appear in the generated PDF or may not
                be suited for pencil and paper.
              </p>
              <p>
                <strong>
                  Always review the generated PDF before distributing to
                  students.
                </strong>
              </p>
            </StyledCautionBanner>
            <StyledInfoBanner>
              The following item types are supported at this time:
              <ul>
                <li>Multiple Choice</li>
                <li>Inline Choice/Entry</li>
                <li>Ext. Text, SCR & ECR</li>
                <li>Gap Match</li>
                <li>Selectable Text</li>
              </ul>
            </StyledInfoBanner>
            <Form.FieldList>
              <Form.Field label="Print Theme">
                <SelectInput
                  value={theme}
                  options={[
                    {
                      label: "Traditional (Times New Roman)",
                      value: "traditional",
                    },
                    { label: "Modern (Verdana)", value: "modern" },
                    { label: "Friendly (Comic Neue)", value: "friendly" },
                  ]}
                  onChange={(e) => setTheme(e?.value ?? "")}
                />
              </Form.Field>
            </Form.FieldList>
            <StyledCheckboxes>
              <StyledCheckboxLabel label="Use Dual Column Layout" layout="row">
                <CheckboxInput
                  checked={printDualColumn}
                  onChange={({ checked }) => setPrintDualColumn(checked)}
                />
              </StyledCheckboxLabel>
            </StyledCheckboxes>
            {props.flags.showShuffleItems && (
              <StyledCheckboxes>
                <StyledCheckboxLabel
                  label="Shuffle Question Order"
                  layout="row"
                >
                  <CheckboxInput
                    checked={printShuffleQuestions}
                    onChange={({ checked }) =>
                      setPrintShuffleQuestions(checked)
                    }
                  />
                </StyledCheckboxLabel>
              </StyledCheckboxes>
            )}
            <StyledCheckboxes>
              <StyledCheckboxLabel label="Print Assessment" layout="row">
                <CheckboxInput
                  checked={printExam}
                  onChange={({ checked }) => setPrintExam(checked)}
                />
              </StyledCheckboxLabel>
              <StyledCheckboxLabel label="Print Answer Key" layout="row">
                <CheckboxInput
                  checked={printAnswerKey}
                  onChange={({ checked }) => setPrintAnswerKey(checked)}
                />
              </StyledCheckboxLabel>
            </StyledCheckboxes>
          </VSpace>
        </PaddedBody>
      </PrintModal>
      <Modal
        title="Feedback"
        {...feedbackModal.modalProps}
        onAfterClose={() => {
          setFeedbackText("");
          setFeedbackValue(undefined);
        }}
        onRequestClose={() => {
          feedbackModal.close();
          props.onRequestClose?.();
        }}
        buttons={
          <SubmitButtons
            onSubmit={handleSubmitFeedback}
            isSubmitDisabled={feedbackValue == null}
            onCancel={() => {
              feedbackModal.close();
              props.onRequestClose?.();
            }}
          />
        }
      >
        <StyledFeedback>
          <PaddedBody>
            <VSpace>
              <Callout>How is the print quality of your PDF?</Callout>
              <FeedbackInput
                value={feedbackValue}
                onChange={(value) => setFeedbackValue(value)}
              />
              <Callout>
                Tell us how we can improve the quality of the PDF.
              </Callout>
              <Form.Field label="Feedback">
                <TextArea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />
              </Form.Field>
            </VSpace>
          </PaddedBody>
        </StyledFeedback>
      </Modal>
    </>
  );
};

export default PrintExamModal;
